import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { auth } from "@/lib/auth";
import CalendarBoard from "@/components/CalendarBoard";
import { scrollToId } from "@/lib/scroll";
import PresskitCustomizer from "@/components/PresskitCustomizer";
import ShareMediaButton from "@/components/ShareMediaButton";
import { Camera, Instagram, ExternalLink, Loader2, CalendarCheck, Edit3, Check, X, Clapperboard, Images, FileDown, Share2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SLUG = "6xo";

// réalisations 6xo — photos + vidéos (Dropbox Transfer, novembre 2026)
const SIXO_PHOTOS = [
    "zve6729.jpg", "zve6732.jpg", "zve6741.jpg", "zve7339.jpg", "zve7340.jpg",
    "zve7348.jpg", "zve7359.jpg", "zve7363.jpg", "zve7379.jpg", "zve7383.jpg",
    "zve7384.jpg", "zve7385.jpg", "zve7391.jpg", "zve7404.jpg", "zve7443.jpg",
    "zve8243.jpg", "zve8244.jpg", "zve8249.jpg", "zve8250.jpg", "zve8251.jpg",
];
const SIXO_VIDEOS = [
    { file: "DA-MOVIE.mp4", title: "DA MOVIE" },
    { file: "beko-000-.mp4", title: "BEKO — 000" },
    { file: "beko-blackberrry-4.mp4", title: "BEKO — BLACKBERRY 4" },
    { file: "ck-lewis.mp4", title: "CK LEWIS" },
    { file: "jokid-w4ht-h4ppens.mp4", title: "JOKID — W4HT H4PPENS" },
    { file: "kamer-2v.mp4", title: "KAMER — 2V" },
    { file: "nore6l-final.mp4", title: "NORE6L — FINAL" },
];

export default function Creator6xo() {
    const { lang } = useLang();
    const [profile, setProfile] = useState(null);
    const [user, setUser] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draftBio, setDraftBio] = useState("");
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [copied, setCopied] = useState(false);
    const [uploaded, setUploaded] = useState([]); // médias uploadés par 6xo depuis son espace
    useEffect(() => {
        fetch(`${API}/api/creator/media`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((d) => setUploaded(d.media || []))
            .catch(() => {});
    }, []);
    const uploadedVideos = uploaded.filter((m) => m.kind === "video");
    const uploadedPhotos = uploaded.filter((m) => m.kind === "photo");

    // lien direct partageable → ouvre la page Artistes directement sur ce profil
    const shareProfile = async () => {
        const url = `${window.location.origin}/#artiste?creator=6xo`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "6xo — GLOOM ENT.", url });
                return;
            }
            throw new Error("no native share");
        } catch {
            try {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2200);
            } catch { /* clipboard bloqué */ }
        }
    };

    useEffect(() => auth.subscribe((s) => setUser(s.user)), []);
    useEffect(() => {
        fetch(`${API}/api/artists/${SLUG}/presskit`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(setProfile)
            .catch(() => setProfile({ artist: "6xo", bio: "", links: {}, presskit: {} }));
    }, []);

    // navigation « CRÉATEUR » : dès que le profil est chargé (layout final),
    // on scrolle pile sur la section — plusieurs passes pour absorber le chargement des images
    useEffect(() => {
        if (!profile || !window.__scrollToCreator) return;
        window.__scrollToCreator = false;
        const target = () => scrollToId("#creator-6xo", -70);
        [80, 500, 1100, 1900].forEach((d) => setTimeout(target, d));
    }, [profile]);

    // scroll direct demandé en session (lien #artiste?creator=6xo ouvert sur la page déjà montée)
    useEffect(() => {
        const fn = () => {
            [60, 400, 900, 1600].forEach((d) => setTimeout(() => scrollToId("#creator-6xo", -70), d));
        };
        window.addEventListener("creator:scroll", fn);
        return () => window.removeEventListener("creator:scroll", fn);
    }, []);

    const mine = user && profile?.owner?.user_id === user.user_id;
    const bio = profile?.presskit?.bio || profile?.bio || "";

    const saveBio = async () => {
        setBusy(true);
        try {
            await auth.fetch(`${API}/api/artists/${SLUG}/presskit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    style: profile?.presskit?.style || "Photographie / Réalisation",
                    city: profile?.presskit?.city || "Genève",
                    color1: profile?.presskit?.color1 || "gold",
                    color2: profile?.presskit?.color2 || "violet",
                    bio: draftBio,
                    links: { instagram: "https://www.instagram.com/6xo.rec" },
                }),
            });
            setProfile((p) => ({ ...p, presskit: { ...(p.presskit || {}), bio: draftBio } }));
            setEditing(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section id="creator-6xo" data-testid="creator-6xo" className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-20" style={{ background: "radial-gradient(50% 40% at 20% 0%, rgba(212,175,55,0.25) 0%, transparent 70%)" }} />
            <div className="relative">
                <FadeUp delay={0.05}>
                    <SectionLabel en="DIRECTED BY 6XO" fr="RÉALISÉ PAR 6XO" />
                </FadeUp>
                <div className="mt-8 grid items-start gap-10 md:grid-cols-[280px_1fr]">
                    {/* logo + réseaux */}
                    <FadeUp delay={0.1}>
                        <div className="flex flex-col items-center text-center">
                            <div className="w-full max-w-[240px]">
                                <img data-testid="creator-logo" src="/img/6xo-logo.png" alt="6xo" className="w-full" />
                            </div>
                            <a data-testid="creator-instagram" href="https://www.instagram.com/6xo.rec" target="_blank" rel="noreferrer"
                                className="mt-5 flex items-center gap-2 border border-fuchsia-500/50 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-fuchsia-400 transition-all hover:bg-fuchsia-500 hover:text-cream">
                                <Instagram size={13} /> @6XO.REC
                            </a>
                            <button data-testid="creator-book-btn" onClick={() => window.__goPage?.("contact")}
                                className="mt-3 flex w-full max-w-[240px] items-center justify-center gap-2 border border-gold bg-gold/10 px-5 py-3 font-mono text-[9px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink">
                                <CalendarCheck size={13} /> {lang === "fr" ? "RÉSERVER AVEC 6XO" : "BOOK WITH 6XO"}
                            </button>
                            <a data-testid="creator-presskit-btn" href={`${API}/api/artists/6xo/presskit.pdf`} target="_blank" rel="noreferrer"
                                className="mt-3 flex w-full max-w-[240px] items-center justify-center gap-2 border border-violet/70 bg-violet/10 px-5 py-3 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream">
                                <FileDown size={13} /> {lang === "fr" ? "DOSSIER PRESSE PDF" : "PRESS KIT PDF"}
                            </a>
                            <button data-testid="creator-share-btn" onClick={shareProfile}
                                className="mt-3 flex w-full max-w-[240px] items-center justify-center gap-2 border border-white/20 px-5 py-3 font-mono text-[9px] tracking-[0.25em] text-cream/60 transition-all hover:border-cream hover:text-cream">
                                {copied ? <Check size={13} className="text-green-400" /> : <Share2 size={13} />}
                                {copied ? (lang === "fr" ? "LIEN COPIÉ !" : "LINK COPIED!") : (lang === "fr" ? "PARTAGER LE PROFIL" : "SHARE PROFILE")}
                            </button>
                        </div>
                    </FadeUp>
                    {/* bio */}
                    <FadeUp delay={0.15}>
                        <div className="border border-white/10 p-7">
                            <div className="flex items-center justify-between">
                                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-gold">
                                    <Camera size={13} /> {lang === "fr" ? "PHOTOGRAPHE / RÉALISATEUR — GENÈVE" : "PHOTOGRAPHER / DIRECTOR — GENEVA"}
                                </p>
                                {mine && (
                                    <button data-testid="creator-edit-btn" onClick={() => { setDraftBio(bio); setEditing(!editing); }}
                                        className="flex items-center gap-1.5 border border-violet/60 px-3 py-1.5 font-mono text-[8px] tracking-[0.2em] text-violet transition-all hover:bg-violet hover:text-cream">
                                        <Edit3 size={11} /> {editing ? (lang === "fr" ? "FERMER" : "CLOSE") : (lang === "fr" ? "MODIFIER MA BIO" : "EDIT MY BIO")}
                                    </button>
                                )}
                            </div>
                            {/* personnalisation du press kit PDF — réservée au propriétaire (6xo) */}
                            {mine && <PresskitCustomizer slug="6xo" lang={lang} />}
                            {editing ? (
                                <div className="mt-5 space-y-3">
                                    <textarea
                                        data-testid="creator-bio-edit"
                                        value={draftBio}
                                        onChange={(e) => setDraftBio(e.target.value)}
                                        rows={6}
                                        className="w-full resize-none border-b border-white/15 bg-transparent py-2 font-serif text-sm italic leading-relaxed text-cream/80 outline-none focus:border-gold"
                                    />
                                    <button data-testid="creator-bio-save" onClick={saveBio} disabled={busy}
                                        className="flex items-center gap-2 border border-gold bg-gold/10 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40">
                                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {lang === "fr" ? "ENREGISTRER" : "SAVE"}
                                    </button>
                                </div>
                            ) : (
                                <p data-testid="creator-bio" className="mt-5 font-serif text-sm italic leading-relaxed text-cream/75">
                                    {bio}
                                </p>
                            )}
                            {saved && <p className="mt-3 font-mono text-[9px] tracking-[0.25em] text-green-400">{lang === "fr" ? "BIO ENREGISTRÉE" : "BIO SAVED"}</p>}
                            {/* liens vers ses travaux */}
                            <div className="mt-7 border-t border-white/10 pt-5">
                                <p className="font-mono text-[9px] tracking-[0.25em] text-cream/50">{lang === "fr" ? "SES TRAVAUX CHEZ GLOOM" : "HIS WORK AT GLOOM"}</p>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <a data-testid="creator-work-cars" href="#videos" onClick={(e) => { e.preventDefault(); window.__openProject = "timelease-reel"; window.__goPage?.("videos"); }}
                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[8px] tracking-[0.2em] text-cream/60 transition-all hover:border-gold hover:text-gold">
                                        <ExternalLink size={11} /> TIMELEASE — CARS
                                    </a>
                                    <a data-testid="creator-work-devagar" href="#videos" onClick={(e) => { e.preventDefault(); window.__openProject = { youtubeId: "5wv8WAtlGx0", title: "JOGA JU — DEVAGAR", category: "CLIP", client: "DIRECTED BY 6XO", year: "2026", desc: { fr: "Clip réalisé par 6xo pour Joga Ju.", en: "Video directed by 6xo for Joga Ju." } }; window.__goPage?.("videos"); }}
                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[8px] tracking-[0.2em] text-cream/60 transition-all hover:border-gold hover:text-gold">
                                        <ExternalLink size={11} /> JOGA JU — DEVAGAR
                                    </a>
                                    <a data-testid="creator-work-laureeta" href="#videos" onClick={(e) => { e.preventDefault(); window.__openProject = { youtubeId: "qRGzsT9ZbPY", title: "LAUREETA — DAME AZZ", category: "CLIP", client: "DIRECTED BY 6XO", year: "2026", desc: { fr: "Clip réalisé par 6xo pour Laureeta.", en: "Video directed by 6xo for Laureeta." } }; window.__goPage?.("videos"); }}
                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[8px] tracking-[0.2em] text-cream/60 transition-all hover:border-gold hover:text-gold">
                                        <ExternalLink size={11} /> LAUREETA — DAME AZZ
                                    </a>
                                </div>
                            </div>
                        </div>
                    </FadeUp>
                </div>

                {/* calendrier partagé admin + 6xo */}
                {mine && (
                    <FadeUp delay={0.2}>
                        <div className="mt-10">
                            <CalendarBoard provider="6xo" />
                        </div>
                    </FadeUp>
                )}

                {/* réalisations — vidéos */}
                <FadeUp delay={0.18} className="mt-14">
                    <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.35em] text-gold">
                        <Clapperboard size={13} /> {lang === "fr" ? "SES RÉALISATIONS VIDÉO" : "HIS VIDEO WORK"}
                    </p>
                    <div className="mt-5 grid gap-5 md:grid-cols-2">
                        {/* vidéos uploadées par 6xo — en premier */}
                        {uploadedVideos.map((m, i) => (
                            <motion.div
                                key={m.media_id}
                                data-testid={`creator-video-up-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                                className="group border border-fuchsia-500/30 bg-white/[0.02]"
                            >
                                <div className="relative aspect-video overflow-hidden bg-black">
                                    <video src={m.url} poster={m.poster || undefined} controls preload="metadata" playsInline className="h-full w-full" />
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                    <p className="truncate font-mono text-[9px] tracking-[0.2em] text-cream/80">{m.filename}</p>
                                    <div className="flex items-center gap-2">
                                        <ShareMediaButton
                                            kind="video"
                                            src={m.url}
                                            poster={m.poster}
                                            title={`6XO — ${m.filename}`}
                                            target="#artiste?creator=6xo"
                                            testid={`share-creator-up-video-${i}`}
                                        />
                                        <span className="shrink-0 font-mono text-[8px] tracking-[0.2em] text-fuchsia-400">NOUVEAU</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                        {SIXO_VIDEOS.map((v, i) => (
                            <motion.div
                                key={v.file}
                                data-testid={`creator-video-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ delay: i * 0.06, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                                className="group border border-white/10 transition-colors hover:border-gold/50"
                            >
                                <div className="relative aspect-video overflow-hidden bg-black">
                                    <video
                                        src={`/video/6xo/${v.file}`}
                                        poster={`/video/6xo/posters/${v.file.replace(".mp4", ".jpg")}`}
                                        controls
                                        preload="none"
                                        playsInline
                                        className="h-full w-full"
                                    />
                                </div>
                                <div className="flex items-center justify-between gap-2 p-3">
                                    <p className="font-mono text-[9px] tracking-[0.2em] text-cream/70">
                                        <span className="mr-2 text-gold">{String(i + 1).padStart(2, "0")}</span>
                                        {v.title}
                                    </p>
                                    <ShareMediaButton
                                        kind="video"
                                        src={`/video/6xo/${v.file}`}
                                        poster={`/video/6xo/posters/${v.file.replace(".mp4", ".jpg")}`}
                                        title={`6XO — ${v.title}`}
                                        target="#artiste?creator=6xo"
                                        testid={`share-creator-video-${i}`}
                                    />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </FadeUp>

                {/* photos */}
                <FadeUp delay={0.22} className="mt-12">
                    <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.35em] text-violet">
                        <Images size={13} /> {lang === "fr" ? "SES PHOTOS" : "HIS PHOTOS"} — {SIXO_PHOTOS.length + uploadedPhotos.length}
                    </p>
                    <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                        {/* photos uploadées par 6xo — en premier */}
                        {uploadedPhotos.map((m, i) => (
                            <motion.div
                                key={m.media_id}
                                data-testid={`creator-photo-up-${i}`}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, margin: "-30px" }}
                                transition={{ delay: (i % 4) * 0.05, duration: 0.45 }}
                                whileHover={{ y: -4 }}
                                onClick={() => setLightbox(m.url)}
                                className="group relative aspect-[3/4] cursor-pointer overflow-hidden border border-fuchsia-500/30 transition-colors hover:border-fuchsia-400"
                            >
                                <img
                                    src={m.url}
                                    alt={`6xo — ${m.filename}`}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <span className="absolute left-1.5 top-1.5 bg-fuchsia-500 px-1.5 py-0.5 font-mono text-[7px] tracking-[0.15em] text-ink">NOUVEAU</span>
                                <span className="absolute bottom-1.5 right-1.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                                    <ShareMediaButton kind="photo" src={m.url} title={`6XO — ${m.filename}`} target="#artiste?creator=6xo" testid={`share-creator-up-photo-${i}`} />
                                </span>
                            </motion.div>
                        ))}
                        {SIXO_PHOTOS.map((p, i) => (
                            <motion.div
                                key={p}
                                data-testid={`creator-photo-${i}`}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true, margin: "-30px" }}
                                transition={{ delay: (i % 4) * 0.05, duration: 0.45 }}
                                whileHover={{ y: -4 }}
                                onClick={() => setLightbox(p)}
                                className="group relative aspect-[3/4] cursor-pointer overflow-hidden border border-white/10 transition-colors hover:border-violet/60"
                            >
                                <img
                                    src={`/img/6xo/${p}`}
                                    alt={`6xo — ${p}`}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <span className="absolute bottom-1.5 right-1.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                                    <ShareMediaButton kind="photo" src={`/img/6xo/${p}`} title={`6XO — PHOTO ${String(i + 1).padStart(2, "0")}`} target="#artiste?creator=6xo" testid={`share-creator-photo-${i}`} />
                                </span>
                            </motion.div>
                        ))}
                    </div>
                </FadeUp>

                {/* lightbox photo */}
                <AnimatePresence>
                    {lightbox && (
                        <motion.div
                            data-testid="creator-lightbox"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[96] flex items-center justify-center bg-ink/95 p-5 backdrop-blur-md"
                            onClick={() => setLightbox(null)}
                        >
                            <button data-testid="creator-lightbox-close" aria-label="Fermer" className="absolute right-5 top-5 text-cream/60 transition-colors hover:text-cream">
                                <X size={22} />
                            </button>
                            <motion.img
                                initial={{ scale: 0.92, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.92, opacity: 0 }}
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                src={lightbox.startsWith("/") || lightbox.startsWith("http") ? lightbox : `/img/6xo/${lightbox}`}
                                alt="6xo"
                                className="max-h-[86vh] max-w-full border border-white/20 object-contain"
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}
